/**
 * inspectionRating — the three-tier inspection rating vocabulary.
 *
 * Mirrors App\Support\InspectionRating on the backend. Each tier names the
 * ACTION it calls for rather than how the inspector felt about the area, which
 * is the entire point of the scale:
 *
 *   good            No action required.
 *   needs_attention Follow up in 1-2 weeks. Review during the standard
 *                   maintenance cycle.
 *   critical        Branch manager escalation. Automated email sent to
 *                   management for intervention.
 *
 * Everything that renders a rating — the picker, badges, list rows, the
 * dashboard, the PDF legend — reads from here, so the wording and colors can
 * never drift between surfaces.
 */

/* ============================================
 * TYPES
 * ============================================ */

/** The three tiers. `null` on an item means "not yet rated". */
export type InspectionRating = 'good' | 'needs_attention' | 'critical';

/** Everything a surface needs to render one tier. */
export interface InspectionRatingMeta {
	value: InspectionRating;
	/** Short label — buttons, badges, table cells. */
	label: string;
	/** What this tier requires someone to do. Shown as guidance and in the PDF. */
	action: string;
	/** Tailwind classes for the selected/active state of a picker button. */
	selectedClasses: string;
	/** Tailwind classes for the unselected state of a picker button. */
	unselectedClasses: string;
	/** Tailwind classes for a read-only badge. */
	badgeClasses: string;
}

/* ============================================
 * VOCABULARY
 * ============================================ */

/**
 * Ordered best-to-worst — this is the render order of the picker, so the
 * least severe option is the easiest to reach and "critical" is a deliberate
 * choice rather than an adjacent mis-tap.
 */
export const INSPECTION_RATINGS: InspectionRatingMeta[] = [
	{
		value: 'good',
		label: 'Good',
		action: 'No action required.',
		selectedClasses: 'bg-green-600 border-green-600 text-white',
		unselectedClasses: 'bg-white border-gray-300 text-green-700 hover:bg-green-50',
		badgeClasses: 'bg-green-100 text-green-800 border-green-300',
	},
	{
		value: 'needs_attention',
		label: 'Needs Attention',
		action: 'Follow up in 1-2 weeks. Review during standard maintenance cycle.',
		selectedClasses: 'bg-amber-500 border-amber-500 text-white',
		unselectedClasses: 'bg-white border-gray-300 text-amber-700 hover:bg-amber-50',
		badgeClasses: 'bg-amber-100 text-amber-800 border-amber-300',
	},
	{
		value: 'critical',
		label: 'Critical',
		action: 'Branch manager escalation. Automated email sent to management for intervention.',
		selectedClasses: 'bg-red-600 border-red-600 text-white',
		unselectedClasses: 'bg-white border-gray-300 text-red-700 hover:bg-red-50',
		badgeClasses: 'bg-red-100 text-red-800 border-red-300',
	},
];

/** Lookup by tier value. */
export const INSPECTION_RATING_MAP: Record<InspectionRating, InspectionRatingMeta> =
	INSPECTION_RATINGS.reduce((acc, meta) => {
		acc[meta.value] = meta;
		return acc;
	}, {} as Record<InspectionRating, InspectionRatingMeta>);

/**
 * Severity order, worst-highest. Used to derive an inspection's overall tier
 * from its items: one critical finding makes the whole inspection critical.
 */
export const RATING_SEVERITY: Record<InspectionRating, number> = {
	good: 0,
	needs_attention: 1,
	critical: 2,
};

/* ============================================
 * HELPERS
 * ============================================ */

/** Display metadata for a tier, or null when the item is unrated. */
export const ratingMeta = (rating: string | null | undefined): InspectionRatingMeta | null =>
	rating && rating in INSPECTION_RATING_MAP
		? INSPECTION_RATING_MAP[rating as InspectionRating]
		: null;

/** Short label for a tier, falling back to "Not rated". */
export const ratingLabel = (rating: string | null | undefined): string =>
	ratingMeta(rating)?.label ?? 'Not rated';

/** Whether a tier demands a written reason. Only Critical does — it escalates. */
export const ratingRequiresNote = (rating: string | null | undefined): boolean =>
	rating === 'critical';

/**
 * The worst tier across a set of ratings, or null when nothing is rated.
 * Matches InspectionRating::worst() on the backend.
 */
export const worstRating = (ratings: Array<string | null | undefined>): InspectionRating | null => {
	let worst: InspectionRating | null = null;
	for (const rating of ratings) {
		const meta = ratingMeta(rating);
		if (!meta) continue;
		if (worst === null || RATING_SEVERITY[meta.value] > RATING_SEVERITY[worst]) {
			worst = meta.value;
		}
	}
	return worst;
};
